import type { FixtureExport } from './fixtureApi.js';
import {
    isSingleFixture,
    isFixtureGroup,
    isFixtureVariants,
    createComponentDefinition,
    type ComponentDefinition,
} from './fixtureApiConsumer.js';
import { FixturePath } from './FixturePath.js';

/**
 * Validates a fixture path option.
 * @throws Error if path contains empty segments
 */
function validatePath(path: string): void {
  if (path.startsWith('/')) {
    throw new Error(`Fixture path cannot start with '/': ${path}`);
  }
  if (path.includes('//')) {
    throw new Error(`Fixture path cannot contain empty segments: ${path}`);
  }
  if (path === '') {
    throw new Error(`Fixture path cannot be empty (use undefined for default)`);
  }
}

/**
 * Computes the tree path segments for a fixture.
 * @param filePath The file path of the fixture
 * @param pathOption The path option from the fixture export
 * @returns Array of path segments for the tree
 */
function computePathSegments(filePath: string, pathOption: string | undefined): string[] {
  const baseName = extractBaseName(filePath);
  
  if (pathOption === undefined) {
    // No path → just filename
    return [baseName];
  } else if (pathOption.endsWith('/')) {
    // Trailing / → prefix, append filename
    const prefix = pathOption.slice(0, -1);
    if (prefix) {
      validatePath(prefix);
      return [...prefix.split('/'), baseName];
    }
    // Edge case: path is just "/" - invalid
    throw new Error(`Fixture path cannot be just '/': ${pathOption}`);
  } else {
    // Exact path, no filename appended
    validatePath(pathOption);
    return pathOption.split('/');
  }
}

/**
 * A node in the fixture tree.
 * Can be a folder (with children), variants (with children, horizontal layout), or a component leaf.
 */
export interface FixtureNode {
  readonly id: FixturePath;

  /** Display name */
  readonly name: string;

  /** Node type: folder for groups, variants for horizontal variant display, component for leaves */
  readonly type: 'folder' | 'variants' | 'component';

  /** Children (for folders and variants) */
  readonly children?: readonly FixtureNode[];

  /** Component definition (only for component nodes) */
  readonly component?: ComponentDefinition;

  /** Labels for categorization and filtering */
  readonly labels: readonly string[];

  /** Source fixture file path — present on skeleton nodes (component is undefined) for on-demand loading */
  readonly sourceFile?: string;
}

/**
 * Creates a fixture tree from a map of fixture exports.
 * Entries with `undefined` value are rendered as skeleton nodes (loaded on demand).
 */
export function createFixtureTree(fixtures: Map<string, FixtureExport>): FixtureNode {
  const root: MutableFixtureNode = {
    id: new FixturePath(''),
    name: 'Components',
    type: 'folder',
    children: [],
    labels: [],
  };

  for (const [filePath, fixtureExport] of fixtures) {
    if (fixtureExport === undefined) {
      // Skeleton: module not yet loaded — place a placeholder node using the file basename
      const name = extractBaseName(filePath);
      const skeletonNode: MutableFixtureNode = {
        id: new FixturePath(name),
        name,
        type: 'component',
        labels: [],
        sourceFile: filePath,
      };
      if (!root.children) root.children = [];
      root.children.push(skeletonNode);
    } else {
      const pathOption = '_path' in fixtureExport ? fixtureExport._path : undefined;
      const segments = computePathSegments(filePath, pathOption);
      addFixtureExportToTree(root, segments, fixtureExport, [], filePath);
    }
  }

  return root;
}

/**
 * Resolves all lazy fixture module loaders in parallel and returns a map of loaded exports.
 * Used by modes that need the full fixture tree upfront (headless, embedded).
 */
export async function resolveFixtureModules(
  modules: Record<string, () => Promise<{ default?: unknown }>>
): Promise<Map<string, FixtureExport>> {
  const result = new Map<string, FixtureExport>();
  await Promise.all(
    Object.entries(modules).map(async ([path, loader]) => {
      const mod = await loader();
      const fixture = mod.default;
      if (fixture && typeof fixture === 'object') {
        result.set(path, fixture as FixtureExport);
      }
    })
  );
  return result;
}

interface MutableFixtureNode {
  id: FixturePath;
  name: string;
  type: 'folder' | 'variants' | 'component';
  children?: MutableFixtureNode[];
  component?: ComponentDefinition;
  labels: string[];
  sourceFile?: string;
}

function extractBaseName(filePath: string): string {
  const fileName = filePath.split('/').pop() ?? filePath;
  // Remove .fixture.tsx, .fixture.ts, .fixture.jsx, .fixture.js
  return fileName.replace(/\.fixture\.(tsx?|jsx?)$/, '');
}

function addFixtureExportToTree(
  parent: MutableFixtureNode,
  pathSegments: string[],
  fixtureExport: FixtureExport,
  pathPrefix: string[] = [],
  sourceFile: string = '',
): void {
  // Navigate/create folders for all segments except the last
  let current = parent;
  const accumulatedPath = [...pathPrefix];
  
  for (let i = 0; i < pathSegments.length - 1; i++) {
    const folderName = pathSegments[i];
    accumulatedPath.push(folderName);
    const folderId = new FixturePath(accumulatedPath.join('/'));
    
    let folder = current.children?.find(
      (c) => c.type === 'folder' && c.name === folderName
    );
    
    if (!folder) {
      folder = {
        id: folderId,
        name: folderName,
        type: 'folder',
        children: [],
        labels: [],
      };
      if (!current.children) {
        current.children = [];
      }
      current.children.push(folder);
    }
    current = folder;
  }
  
  // Now add the fixture at the final segment
  const finalSegment = pathSegments[pathSegments.length - 1];
  accumulatedPath.push(finalSegment);
  const fullId = new FixturePath(accumulatedPath.join('/'));
  
  if (isSingleFixture(fixtureExport)) {
    // Single fixture - add as leaf
    const def = createComponentDefinition(fixtureExport, fullId.value, finalSegment, sourceFile);
    const componentNode: MutableFixtureNode = {
      id: fullId,
      name: finalSegment,
      type: 'component',
      component: def,
      labels: [...def.labels],
    };
    if (!current.children) {
      current.children = [];
    }
    current.children.push(componentNode);
  } else if (isFixtureGroup(fixtureExport)) {
    // Fixture group - create folder and recurse for entries
    let folder = current.children?.find(
      (c) => c.type === 'folder' && c.name === finalSegment
    );
    
    if (!folder) {
      folder = {
        id: fullId,
        name: finalSegment,
        type: 'folder',
        children: [],
        labels: [...(fixtureExport._labels ?? [])],
      };
      if (!current.children) {
        current.children = [];
      }
      current.children.push(folder);
    }

    // Add each entry in the group - recurse with accumulated path as prefix
    for (const [key, entry] of Object.entries(fixtureExport._entries)) {
      addFixtureExportToTree(folder, [key], entry, accumulatedPath, sourceFile);
    }
  } else if (isFixtureVariants(fixtureExport)) {
    // Fixture variants - create variants node with children
    const variantsNode: MutableFixtureNode = {
      id: fullId,
      name: finalSegment,
      type: 'variants',
      children: [],
      labels: [...(fixtureExport._labels ?? [])],
    };
    
    if (!current.children) {
      current.children = [];
    }
    current.children.push(variantsNode);

    // Add each variant as a child component
    for (const [key, variant] of Object.entries(fixtureExport._variants)) {
      const childId = new FixturePath([...accumulatedPath, key].join('/'));
      const componentNode: MutableFixtureNode = {
        id: childId,
        name: key,
        type: 'component',
        component: createComponentDefinition(variant, childId.value, key, sourceFile),
        labels: [...(variant._options.labels ?? [])],
      };
      variantsNode.children!.push(componentNode);
    }
  }
}

/**
 * Finds a node in the tree by path.
 */
export function findNodeByPath(root: FixtureNode, path: string[]): FixtureNode | undefined {
  if (path.length === 0) {
    return root;
  }

  let current: FixtureNode | undefined = root;
  
  for (const segment of path) {
    if (!current || !current.children) {
      return undefined;
    }
    current = current.children.find((c) => c.name === segment);
  }

  return current;
}

/**
 * Collects all component nodes from a subtree.
 */
export function collectComponents(node: FixtureNode): ComponentDefinition[] {
  if (node.type === 'component' && node.component) {
    return [node.component];
  }

  const components: ComponentDefinition[] = [];
  for (const child of node.children ?? []) {
    components.push(...collectComponents(child));
  }
  return components;
}
